/**
 * FooterEditorPanel — dedicated Footer editor (Phase 11.6.1). Exposes exactly the controls
 * the production Footer component (`site-builder/components/footer/Footer.tsx`, a literal
 * clone of `__reference__/realDemo/footer-landing`) actually renders: brand name, navigation
 * links, contact details, social links, an auto-computed copyright line, and visibility. No
 * tagline field — the reference footer name block is title-only (no tagline), so inventing
 * one would edit nothing.
 *
 * No Layout control — the Footer now has exactly one supported layout (`default`); the
 * registry no longer lists `extended`/`minimal` as selectable (see registerBuiltins.ts).
 * They remain valid `FooterLayout` schema values for backward compatibility with old data,
 * but nothing produces them anymore and there is no styling left to select.
 *
 * Contact and social fields are builder metadata (`schema.businessInfo`), mirrored onto the
 * rendered footer/contact content by the SAME setters the Settings screen uses
 * (`setBusinessField` / `setSocial`) — reused here, not duplicated, so both screens and the
 * persisted reload path stay in lockstep. Social icons are fully dynamic: adding a URL shows
 * its platform icon immediately, clearing it removes the icon immediately (Footer.tsx
 * URL-detects the platform on every render — no manual selection, no stale state).
 */
import { Plus, Trash2 } from 'lucide-react'
import { useBuilderEditor, SOCIAL_PLATFORM_FIELDS } from './editorContext'
import { Fieldset, TextField, ToggleField, ReadOnlyRow } from './controls'

export function FooterEditorPanel({ sectionId }: { sectionId: string }) {
  const e = useBuilderEditor()
  const section = e.schema.pages[0]?.sections.find((s) => s.id === sectionId)
  if (!section) {
    return <p className="px-5 py-6 text-xs text-gray-400">This section is no longer available.</p>
  }

  const links = section.content.items ?? []
  const info = e.schema.businessInfo ?? {}
  const brand = section.content.heading ?? ''
  const year = new Date().getFullYear()

  return (
    <div>
      <Fieldset title="Visibility">
        <ToggleField
          label="Show footer"
          description="Hide to remove it from the published site"
          checked={section.visible}
          onChange={() => e.toggleSectionVisible(sectionId)}
        />
      </Fieldset>

      <Fieldset title="Brand">
        <TextField label="Brand name" value={brand} onChange={(v) => e.setSectionText(sectionId, 'heading', v)} />
      </Fieldset>

      <Fieldset title="Navigation links">
        {links.map((it) => (
          <div key={it.id} className="flex flex-col gap-2 rounded-xl border border-gray-100 p-3">
            <TextField label="Label" value={it.title} onChange={(v) => e.setNavLink(sectionId, it.id, 'title', v)} />
            <TextField label="Link" value={it.value ?? ''} onChange={(v) => e.setNavLink(sectionId, it.id, 'value', v)} />
            <button
              type="button"
              onClick={() => e.removeItem(sectionId, it.id)}
              className="flex items-center gap-1 self-start rounded-lg px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => e.addItem(sectionId)}
          className="flex items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50"
        >
          <Plus className="h-3.5 w-3.5" /> Add link
        </button>
      </Fieldset>

      <Fieldset title="Contact">
        <TextField label="Phone" value={info.phone ?? ''} placeholder="+1 555 123 4567" onChange={(v) => e.setBusinessField('phone', v)} />
        <TextField label="Email" value={info.email ?? ''} placeholder="hello@business.com" onChange={(v) => e.setBusinessField('email', v)} />
        <TextField label="Address" value={info.street ?? ''} placeholder="123 Main St" onChange={(v) => e.setBusinessField('street', v)} />
      </Fieldset>

      <Fieldset title="Social links">
        {SOCIAL_PLATFORM_FIELDS.map((p) => (
          <TextField
            key={p.key}
            label={p.label}
            value={info.social?.[p.key] ?? ''}
            placeholder={p.placeholder}
            onChange={(v) => e.setSocial(p.key, v)}
          />
        ))}
      </Fieldset>

      <Fieldset title="Copyright">
        <ReadOnlyRow label="Preview" value={brand ? `© ${year} ${brand}` : `© ${year}`} />
        <p className="text-[11px] leading-relaxed text-gray-400">
          Generated automatically from the current year and the brand name above.
        </p>
      </Fieldset>
    </div>
  )
}
